import { Link } from 'react-router-dom'
import { useState, useEffect, useMemo } from 'react'
import Disclaimer from '../components/Common/Disclaimer'
import ThemeToggle from '../components/Common/ThemeToggle'
import SettingsDialog from '../components/Common/SettingsDialog'
import CoffeeButton from '../components/Common/CoffeeButton'
import Icon from '../components/Common/Icon'
import QuickStartBanner from '../components/Common/QuickStartBanner'
import AchievementPanel from '../components/Common/AchievementPanel'
import {
  chapters,
  isChapterUnlocked,
  isChapterCompleted,
  getChapterCardIds,
} from '../data/chapters'
import { useGameStore } from '../store/gameStore'
import { evaluateAchievements } from '../engine/Achievements'

function HomePage() {
  const [showDisclaimer, setShowDisclaimer] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const {
    getStatistics,
    getBehaviorPatterns,
    progressTracker,
    unlockedAchievements,
    recordAchievementUnlock,
    mistakeEntries,
    guestBookEntries,
    isInitialized,
    initialize,
    unlockVersion,
  } = useGameStore()
  const stats = isInitialized ? getStatistics() : null

  useEffect(() => {
    if (!isInitialized) {
      initialize()
    }
  }, [isInitialized, initialize])

  useEffect(() => {
    const hasSeen = localStorage.getItem('hasSeenDisclaimer')
    if (!hasSeen) {
      setShowDisclaimer(true)
    }
  }, [])

  const handleDisclaimerClose = () => {
    localStorage.setItem('hasSeenDisclaimer', 'true')
    setShowDisclaimer(false)
  }

  // 章节折叠状态 - 默认展开第一个解锁的章节
  const [expandedChapterId, setExpandedChapterId] = useState<number | null>(() => {
    const stored = localStorage.getItem('expandedChapterId')
    if (stored !== null) return parseInt(stored, 10)
    return 1
  })

  const toggleChapter = (id: number) => {
    setExpandedChapterId(prev => {
      const next = prev === id ? null : id
      localStorage.setItem('expandedChapterId', String(next))
      return next
    })
  }

  // 章节解锁状态（依赖 unlockVersion 确保解锁后立即重新计算）
  // unlockVersion 表面上在函数体内未引用, 但解构它在 deps 数组里是为了让
  // progressTracker 内部状态变化时强制 useMemo 重新计算 (绕过引用稳定性问题)
  const progressMap = useMemo(
    () =>
      isInitialized
        ? Object.fromEntries(
            Array.from(progressTracker.getAllProgress()).map(([k, v]) => [k, { mastery: v.mastery }])
          )
        : {},
    // eslint-disable-next-line react-hooks/exhaustive-deps -- unlockVersion 是强制重算信号, 见上方注释
    [isInitialized, progressTracker, unlockVersion]
  )

  // 勋章解锁状态
  const unlockedMap = useMemo(() => {
    if (!stats) return {}
    const chapterCardIds: Record<number, string[]> = {}
    for (let i = 1; i <= 5; i++) {
      chapterCardIds[i] = getChapterCardIds(i)
    }
    const behavior = getBehaviorPatterns()
    const { status } = evaluateAchievements({
      progress: progressMap as never,
      totalAttempts: stats.totalAttempts,
      correctRate: stats.correctRate,
      averageDecisionTimeMs: behavior.averageDecisionTimeMs,
      chapterCardIds,
      isChapterUnlocked: (id) => isChapterUnlocked(id, progressMap),
    })
    for (const [id, ok] of Object.entries(status)) {
      if (ok && !unlockedAchievements[id]) {
        recordAchievementUnlock(id)
      }
    }
    const map: Record<string, number> = { ...unlockedAchievements }
    for (const [id, ok] of Object.entries(status)) {
      map[id] = ok ? (map[id] ?? Date.now()) : 0
    }
    return map
  }, [progressMap, stats, unlockedAchievements, getBehaviorPatterns, recordAchievementUnlock])

  const accuracy = stats ? Math.round(stats.correctRate * 100) : 0
  const completedCards = stats?.completedCards ?? 0
  const totalCards = stats?.totalCards ?? 0

  // 判断主行动：如果有未完成的学习，显示"继续学习"，否则显示"开始训练"
  const hasStarted = completedCards > 0
  const mainActionText = hasStarted ? '继续学习' : '开始训练'
  const mainActionLink = hasStarted ? `/study/1/s1` : `/study/1/s1`

  return (
    <div className="min-h-screen p-4 pb-8 md:p-8 crt-scanlines">
      <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <div className="max-w-4xl mx-auto">
        {/* ============ 顶部栏 ============ */}
        <header className="pixel-box p-4 mb-6 pixel-box-bright">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h1 className="font-pixel text-lg md:text-xl pixel-title text-[color:var(--color-bull)] mb-1">
                Z 家军
              </h1>
              <p className="font-pixel text-[10px] text-[color:var(--color-text-dim)] tracking-widest">
                交易训练场 v0.1
              </p>
            </div>
            <div className="flex items-center gap-2">
              <CoffeeButton />
              <ThemeToggle />
              <button
                onClick={() => setSettingsOpen(true)}
                aria-label="打开设置"
                title="设置"
                className="pixel-button pixel-tag inline-flex items-center gap-1.5 group"
                style={{ fontSize: '11px', padding: '6px 10px', minWidth: 'auto' }}
              >
                <Icon name="gear" size={14} className="transition-transform duration-500 group-hover:rotate-90" />
                <span>设置</span>
              </button>
            </div>
          </div>
        </header>

        {/* ============ 主行动区 ============ */}
        <Link
          to={mainActionLink}
          className="block pixel-box pixel-box-bright p-6 mb-6 hover:bg-[color:var(--color-bg-elevated)] transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h2 className="font-pixel text-xl text-[color:var(--color-gold)] mb-2 inline-flex items-center gap-2">
                <Icon name="sparkle" size={16} className="pixel-blink" />
                {mainActionText}
              </h2>
              <p className="font-pixel-body text-sm text-[color:var(--color-text)]">
                {hasStarted
                  ? `已完成 ${completedCards}/${totalCards} 张卡片，正确率 ${accuracy}%`
                  : '从第一章开始，练习择时 · 买点识别 · 仓位管理 · 止损纪律'}
              </p>
              {hasStarted && stats && (
                <div className="mt-3 pixel-progress max-w-xs" style={{ height: '8px' }}>
                  <div
                    className="pixel-progress-fill"
                    style={{ width: `${Math.round((completedCards / totalCards) * 100)}%` }}
                  />
                </div>
              )}
            </div>
          </div>
        </Link>

        {/* ============ 新人引导 ============ */}
        <QuickStartBanner />

        {/* ============ 功能入口网格 ============ */}
        <h3 className="font-pixel text-[10px] text-[color:var(--color-text-bright)] mb-3 tracking-wider">
          ► 功能入口
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
          <FeatureCard
            icon="book"
            title="章节学习"
            desc="14 章 351 张卡片"
            to="/study/1/s1"
            color="var(--color-bull)"
          />
          <FeatureCard
            icon="target"
            title="错题本"
            desc={`${mistakeEntries.length} 道待复习`}
            to="/mistakes"
            color="var(--color-bear)"
            badge={mistakeEntries.length > 0 ? mistakeEntries.length : undefined}
          />
          <FeatureCard
            icon="scroll"
            title="答案之书"
            desc="Z 哥语录 · 抽一签"
            to="/oracle"
            color="var(--color-cyan)"
          />
          <FeatureCard
            icon="teacup"
            title="茶馆"
            desc="心得 · 持股笔记"
            to="/teahouse"
            color="var(--color-gold)"
            badge={guestBookEntries.length > 0 ? guestBookEntries.length : undefined}
          />
          <FeatureCard
            icon="chart"
            title="复盘报告"
            desc="进度 · 薄弱项"
            to="/review"
            color="var(--color-bull)"
          />
          <FeatureCard
            icon="trophy"
            title="勋章墙"
            desc={`${Object.values(unlockedMap).filter(v => v > 0).length} 枚已解锁`}
            onClick={() => {
              const el = document.getElementById('achievement-panel')
              if (el) el.scrollIntoView({ behavior: 'smooth' })
            }}
            color="var(--color-gold)"
          />
        </div>

        {/* ============ 章节地图 ============ */}
        <h3 className="font-pixel text-[10px] text-[color:var(--color-text-bright)] mb-3 tracking-wider">
          ► 章节地图
        </h3>

        {chapters.map((chapter) => {
          const unlocked = isChapterUnlocked(chapter.id, progressMap)
          const completed = isChapterCompleted(chapter.id, progressMap)
          const chapterCardIds = getChapterCardIds(chapter.id)
          const masteredCount = chapterCardIds.filter(
            id => (progressMap[id]?.mastery ?? 0) >= 60
          ).length
          const progressPct = chapterCardIds.length > 0
            ? Math.round((masteredCount / chapterCardIds.length) * 100)
            : 0
          const isExpanded = expandedChapterId === chapter.id

          return (
            <div
              key={chapter.id}
              className={`pixel-box mb-3 overflow-hidden ${
                !unlocked ? 'opacity-50' : ''
              }`}
            >
              <button
                type="button"
                onClick={() => unlocked && toggleChapter(chapter.id)}
                disabled={!unlocked}
                className={
                  'w-full text-left p-3 bg-[color:var(--color-bg-elevated)] border-b-3 border-[color:var(--color-border)] ' +
                  (unlocked ? 'hover:bg-[color:var(--color-bg-base)] cursor-pointer transition-colors' : 'cursor-not-allowed')
                }
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="font-pixel-display text-sm text-[color:var(--color-bull)] mb-1">
                      第 {String(chapter.id).padStart(2, '0')} 章
                    </div>
                    <h4 className="font-pixel-body text-base font-bold text-[color:var(--color-text-bright)]">
                      {chapter.title}
                    </h4>
                    <p className="font-pixel-body text-xs text-[color:var(--color-text-dim)] mt-1">
                      {chapter.description}
                    </p>
                    {unlocked && chapterCardIds.length > 0 && (
                      <div className="mt-2 flex items-center gap-2">
                        <div className="pixel-progress flex-1 max-w-[180px]" style={{ height: '8px' }}>
                          <div
                            className="pixel-progress-fill"
                            style={{ width: `${progressPct}%` }}
                          />
                        </div>
                        <span className="font-pixel-display text-[9px] text-[color:var(--color-text-dim)]">
                          {masteredCount}/{chapterCardIds.length}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`pixel-tag ${
                        completed
                          ? 'text-[color:var(--color-bear)] border-[color:var(--color-bear)]'
                          : !unlocked
                          ? 'text-[color:var(--color-text-dim)] border-[color:var(--color-text-dim)]'
                          : 'text-[color:var(--color-bull)] border-[color:var(--color-bull)]'
                      }`}
                    >
                      {completed ? (
                        <span className="inline-flex items-center gap-1">
                          <Icon name="check" size={10} />
                          已完成
                        </span>
                      ) : !unlocked ? (
                        <span className="inline-flex items-center gap-1">
                          <Icon name="lock" size={10} />
                          锁定
                        </span>
                      ) : (
                        `${chapter.sections.length} 节`
                      )}
                    </span>
                    <span
                      className="font-pixel text-lg text-[color:var(--color-cyan)] w-6 text-center transition-transform"
                      style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
                    >
                      ▶
                    </span>
                  </div>
                </div>
              </button>

              {isExpanded && unlocked && (
                <div className="divide-y-3 divide-[color:var(--color-border)]">
                  {chapter.sections.map((section, idx) => {
                    const sectionMastered = section.cardIds.filter(
                      id => (progressMap[id]?.mastery ?? 0) >= 60
                    ).length
                    return (
                      <Link
                        key={section.id}
                        to={`/study/${chapter.id}/${section.id}`}
                        className="block p-3 hover:bg-[color:var(--color-bg-elevated)] transition-colors group"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3 flex-1">
                            <span className="font-pixel text-sm text-[color:var(--color-gold)] w-6">
                              {String(idx + 1).padStart(2, '0')}
                            </span>
                            <div className="flex-1">
                              <div className="font-pixel-body text-base text-[color:var(--color-text-bright)] group-hover:text-[color:var(--color-bull)]">
                                {section.title}
                              </div>
                              <div className="font-pixel-body text-xs text-[color:var(--color-text-dim)] mt-0.5">
                                {section.cardIds.length} 张卡片
                                {sectionMastered > 0 && (
                                  <span className="ml-2 text-[color:var(--color-bear)] inline-flex items-center gap-1">
                                    <Icon name="star" size={10} />
                                    {sectionMastered} 已掌握
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <span className="font-pixel text-base text-[color:var(--color-cyan)] group-hover:translate-x-1 transition-transform">
                            ▶
                          </span>
                        </div>
                      </Link>
                    )
                  })}
                </div>
              )}

              {!unlocked && chapter.prerequisites && (
                <div className="p-3 text-center font-pixel-body text-xs text-[color:var(--color-text-dim)] flex items-center justify-center gap-1.5">
                  <Icon name="lock" size={11} />
                  完成第 {String(chapter.prerequisites[0]).padStart(2, '0')} 章后解锁
                </div>
              )}
            </div>
          )
        })}

        {/* ============ 勋章面板 ============ */}
        <div id="achievement-panel">
          {Object.keys(unlockedMap).length > 0 && (
            <AchievementPanel unlockedMap={unlockedMap} />
          )}
        </div>

        {/* ============ 底部 ============ */}
        <footer className="text-center font-pixel-display text-[10px] text-[color:var(--color-text-dim)] py-6 mt-6">
          © 2026 Z 家军 · 交易训练场 v0.1
        </footer>
      </div>

      {showDisclaimer && <Disclaimer onClose={handleDisclaimerClose} />}
    </div>
  )
}

function FeatureCard({
  icon,
  title,
  desc,
  to,
  color,
  badge,
  onClick,
}: {
  icon: 'book' | 'target' | 'scroll' | 'teacup' | 'chart' | 'trophy'
  title: string
  desc: string
  to?: string
  color: string
  badge?: number
  onClick?: () => void
}) {
  const content = (
    <>
      <div className="flex items-center justify-between mb-2">
        <Icon name={icon} size={20} style={{ color }} />
        {badge !== undefined && (
          <span className="pixel-tag text-[9px]" style={{ color, borderColor: color }}>
            {badge}
          </span>
        )}
      </div>
      <div className="font-pixel text-sm text-[color:var(--color-text-bright)] mb-1">{title}</div>
      <div className="font-pixel-body text-[10px] text-[color:var(--color-text-dim)]">{desc}</div>
    </>
  )

  if (to) {
    return (
      <Link
        to={to}
        className="pixel-box p-3 hover:bg-[color:var(--color-bg-elevated)] transition-all hover:scale-[1.02]"
      >
        {content}
      </Link>
    )
  }

  if (onClick) {
    return (
      <button
        onClick={onClick}
        className="pixel-box p-3 hover:bg-[color:var(--color-bg-elevated)] transition-all hover:scale-[1.02] text-left w-full"
      >
        {content}
      </button>
    )
  }

  return null
}

export default HomePage
